import type { MailboxInfo } from 'owa-client-types';
import { createAppButton, createAppFlyoutAnchor, createAppMenuItem } from 'owa-command-ribbon';
import { getComposeRibbonId } from 'owa-compose-ribbon-utils/lib/utils/getComposeRibbonId';
import type { IButtonStyles, IContextualMenuItem, IContextualMenuProps } from '@fluentui/react';
import type { AddinCommandSurfaceItem, AddinLocation } from 'owa-addins-types';
import { getComputedCallback } from 'owa-ribbon-mobx-utils/lib/getComputedCallback';
import { getAddinIcon } from 'owa-addins-surface-actions/lib/utils/getAddinIcon';
import { lazyRibbonPinnedMessageExtensionOnExecute } from 'owa-message-extension-flyout';
import { isOffline } from 'owa-offline';
import { lazyShowOfflineModeDialog } from 'owa-addins-view';
import type {
    AppButtonOnExecuteParameter,
    RibbonControlProps,
    MenuItemControlProps,
    MenuSection,
} from 'owa-acui';
import { owaComputedFn } from 'owa-computed-fn';
import { ContextualMenuItemType } from '@fluentui/react';
import { getComposeHostItemIndex } from 'owa-addins-store/lib/utils/hostItemIndexUtils';
import { getDefaultRibbonStyles } from 'owa-command-ribbon-styles/lib/util/getDefaultRibbonStyles';
import { getAddinSubMenuProps } from 'owa-addins-store/lib/selectors/getAddinSurface';

/**
 * Formats the add-in commands into menu sections for use in a flyout anchor
 * @param addin The add-in command surface item
 * @param uniqueIdBase A unique identifier base to ensure menu item IDs are unique
 * @param addinLocation The location context for the add-in
 * @param addinSubMenuProps The submenu properties for the add-in
 * @param mailboxInfo The mailbox information for telemetry
 * @returns An array of MenuSection objects representing the add-in commands
 */
export const getAddinsSection: (
    addin: AddinCommandSurfaceItem,
    uniqueIdBase: string,
    addinLocation: AddinLocation,
    addinSubMenuProps: IContextualMenuProps | undefined,
    mailboxInfo: MailboxInfo
) => MenuSection[] = owaComputedFn(
    (
        addin: AddinCommandSurfaceItem,
        uniqueIdBase: string,
        addinLocation: AddinLocation,
        addinSubMenuProps: IContextualMenuProps | undefined,
        mailboxInfo: MailboxInfo
    ): MenuSection[] => {
        const menuItems: MenuItemControlProps[] = [];
        let items: IContextualMenuItem[] | undefined = [];

        if (addin.sectionProps?.items) {
            items = addin.sectionProps.items;
        } else if (addinSubMenuProps?.items) {
            items = addinSubMenuProps.items[0].sectionProps?.items;
        }

        items?.forEach((menuItem: IContextualMenuItem) => {
            if (
                menuItem.itemType != ContextualMenuItemType.Divider &&
                menuItem.id &&
                menuItem.name
            ) {
                const localUniqueId = menuItem.id + addin.id;

                if (menuItem.subMenuProps) {
                    const subMenuItems: MenuSection[] = getAddinsSection(
                        menuItem,
                        uniqueIdBase,
                        addinLocation,
                        menuItem.subMenuProps,
                        mailboxInfo
                    );

                    if (subMenuItems.length > 0) {
                        const flyoutId = localUniqueId + uniqueIdBase;
                        menuItems.push(
                            createAppFlyoutAnchor(
                                flyoutId,
                                menuItem.text ?? menuItem.name ?? '' /* label */,
                                {
                                    sections: subMenuItems,
                                },
                                mailboxInfo,
                                undefined /* keytipProps */,
                                getAddinIcon(menuItem, true /* isMenuItem*/) ?? '' /* icon */,
                                undefined /* iconColor */,
                                getDefaultRibbonStyles(),
                                {
                                    customTooltip: menuItem.title,
                                }
                            )
                        );
                    }
                } else {
                    const uniqueId = localUniqueId + uniqueIdBase;

                    menuItems.push(
                        createAppMenuItem(
                            uniqueId,
                            menuItem.text ?? menuItem.name ?? '' /* label */,
                            () => {
                                menuItem.getClickHandler(addinLocation)();
                            },
                            mailboxInfo,
                            getAddinIcon(menuItem, true /* isMenuItem*/) ?? '' /* icon */,
                            undefined /* iconColor */,
                            getDefaultRibbonStyles(),
                            {
                                customTooltip: menuItem.title,
                            }
                        )
                    );
                }
            }
        });

        return [{ controls: menuItems }];
    }
);

// Compose buttons
export function createPinnedAddInButton(
    addIn: AddinCommandSurfaceItem,
    composeId: string,
    editorId: string,
    controlId: number,
    targetWindow: Window,
    styles: IButtonStyles,
    mailboxInfo: MailboxInfo
): RibbonControlProps {
    const composeRibbonId = getComposeRibbonId(controlId, editorId);
    const cacheKey = `${addIn.key.toLowerCase()}_${'MessageCompose'}`;
    const addinSubMenuProps: IContextualMenuProps | undefined = getAddinSubMenuProps(cacheKey);
    if (addinSubMenuProps) {
        const hostItemIndex = getComposeHostItemIndex(composeId);
        const addinLocation = {
            hostItemIndex,
            targetWindow,
        };
        const menuItems: MenuSection[] = getAddinsSection(
            addIn,
            composeRibbonId as string,
            addinLocation,
            addinSubMenuProps,
            mailboxInfo
        );
        if (menuItems.length > 0) {
            return createAppFlyoutAnchor(
                composeRibbonId /* controlId */,
                addIn?.title ?? addIn?.name ?? '' /* label */,
                { sections: menuItems } /* menuDefinition */,
                mailboxInfo,
                undefined /* keyTip */,
                getAddinIcon(addIn) ?? '' /* icon */,
                undefined /* iconColor */,
                styles,
                {
                    customTooltip: addIn.ariaLabel,
                    disabled: isOffline(),
                    onExecute: async (_params?: AppButtonOnExecuteParameter) => {
                        if (isOffline()) {
                            await lazyShowOfflineModeDialog.importAndExecute(targetWindow);
                        }
                    },
                } /* overrides */
            );
        }
    }
    return createAppButton({
        id: composeRibbonId /* controlId */,
        label: addIn?.title ?? addIn?.name ?? '',
        onExecute: (params?: AppButtonOnExecuteParameter) => {
            if (params) {
                getComputedCallback(
                    controlId,
                    lazyRibbonPinnedMessageExtensionOnExecute.importAndExecute,
                    addIn.key,
                    targetWindow,
                    'MessageCompose' /* entrypoint */,
                    params,
                    '' /* itemId */,
                    composeId,
                    mailboxInfo
                )();
            }
        },
        icon: getAddinIcon(addIn) ?? '' /* iconName */,
        styles,
        mailboxInfo,
        overrides: {
            customTooltip: addIn.ariaLabel?.replace(/\s-\s/g, '\n\n'),
            ariaDescription: addIn.ariaLabel,
            disabled: isOffline(),
        },
    });
}
